/**
 * ProductCard — catalog grid card for a public product.
 *
 * Spec ref: SPEC-081 Fase 3 §6.5 (Product card), §5.1 (public market
 *           routes), SPEC-NEGOCIO §10.4 (INV-MKT-20 admin-approval gate —
 *           only `status='approved'` products reach this component),
 *           §10.6-§10.8 (product types: physical/digital/service).
 *           ADR-039 + enmienda INV-MKT-21 (aprobada por Founder 2026-08-28).
 *
 * Layout (mirrors Fase 2 `<PublicStoreCard />` shape, adapted for products):
 *   - Image (first image OR placeholder icon when `images.length === 0`)
 *   - Title (truncated to 2 lines via `line-clamp-2`)
 *   - Price (in créditos — 1 credit = 1 USDT per ADR-039 §2)
 *   - Seller storeName (truncated to 1 line)
 *   - Category badge (from `MARKET_CATEGORIES[].category` slug — shown
 *     title-cased for readability; NOT invented — the canonical labels
 *     live in SPEC-NEGOCIO §10.11)
 *   - Rating (★ + decimal — only rendered when `rating > 0`; Fase 3
 *     returns `rating: 0` by default since reviews are Fase 6 — the
 *     spec says "Rating (if any)" so we hide the badge when 0)
 *   - Type badge (physical/digital/service — color-coded UX affordance;
 *     the backend independently enforces type-specific rules)
 *   - Whole card is a `<Link>` to the public product detail page
 *     `/market/producto/{storeSlug}/{slug}` (typedRoutes-safe via
 *     `as Route` cast per TRAMPA P-067 / CM-51)
 *
 * §6 Anti-Invención:
 *   - NO `as any` (P-041) — dynamic href cast with `as Route` (typed).
 *   - NO `console.*` (P-042).
 *   - ARIA labels (P-044): the wrapping `<Link>` has an `aria-label` that
 *     includes the product title + seller storeName + price (so screen
 *     readers announce the full context — the inner elements are
 *     `aria-hidden="true"` to avoid double-reads).
 *   - The category label is derived from the slug via the shared
 *     `formatCategoryLabel` helper from `@/lib/market-labels` (NOT
 *     invented — it just title-cases the slug "alimentos" → "Alimentos";
 *     "hogar-y-muebles" → "Hogar y Muebles" with conjunctions lowercase;
 *     the canonical labels live in SPEC-NEGOCIO §10.11 + are NOT
 *     displayed verbatim here to avoid duplicating the constant in the
 *     frontend; the product detail page shows the full canonical label).
 *     The catalog badge is a UX affordance, not the source of truth.
 *
 * Spec ref: SPEC-081 Fase 3 §6.5, ADR-039, SPEC-NEGOCIO §10.11.
 */

import Link from 'next/link';
import type { Route } from 'next';
import { ImageOff, Star, Package, FileCode, Wrench, Repeat } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
// SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 (S1-QW1) — shared slug→label
// helper (DRY fix de raíz per CR-CHECKLIST §H2). Replaces the local
// `slugToLabel` that previously duplicated the conjunction logic.
import { formatCategoryLabel } from '@/lib/market-labels';
// SPEC-148 (Sprint 72, 2026-09-07) — F-004a: Wishlist Market.
import { WishlistButton } from '@/components/market/wishlist-button';
import type { PublicProductResponse, ProductType } from '@profileia/shared';

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Type badge styling per `ProductType` (SPEC-081 Fase 3 §3.1).
 *
 * §6 Anti-Invención: the type values come from `ProductType` (Task A) —
 * NOT invented here. The colors are UX affordances (the backend
 * independently enforces type-specific validation per §3.1).
 *
 *   - 'physical' → blue (tangible — the most common type)
 *   - 'digital'  → purple (downloadable — intangible)
 *   - 'service'  → amber (bookable — calendar-based)
 */
const TYPE_BADGE: Record<ProductType, { label: string; variant: 'default' | 'secondary' | 'outline'; icon: typeof Package }> = {
  physical: { label: 'Físico', variant: 'default', icon: Package },
  digital: { label: 'Digital', variant: 'secondary', icon: FileCode },
  service: { label: 'Servicio', variant: 'outline', icon: Wrench },
};

// ─── Component ───────────────────────────────────────────────────────────────

export interface ProductCardProps {
  /** The public product to render. */
  product: PublicProductResponse;
  /**
   * Optional className applied to the root `<Link>`. Used by the catalog
   * grid to apply responsive grid sizing (the card itself fills its
   * container width).
   */
  className?: string;
}

export function ProductCard({ product, className }: ProductCardProps) {
  const href = `/market/producto/${product.storeSlug}/${product.slug}` as Route;
  const typeBadge = TYPE_BADGE[product.type];
  const categoryLabel = formatCategoryLabel(product.category);
  const hasRating = product.rating > 0;
  const image = product.images[0];
  const ariaLabel = `Ver producto ${product.title} de ${product.seller.storeName}, precio ${product.priceCredits} créditos`;

  return (
    <Link
      href={href}
      aria-label={ariaLabel}
      className={cn(
        'group flex flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
    >
      {/* ── Image (first image OR placeholder) ── */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
 {/* (Sprint 72, 2026-09-07) — F-004a: Wishlist ♡ button */}
        <WishlistButton productId={product.productId} />
        {image ? (
          // eslint-disable-next-line @next/next/no-img-element
          // NOTE: we use a plain <img> (not next/image) here because the
          // image URL is a runtime-uploaded asset (S3 / R2 / local fs —
          // Fase 3 Task B market-image.service) and is NOT in the Next.js
          // image optimizer allowlist. Optimizing these would require
          // configuring remotePatterns + a CDN — deferred to Fase 5.
          <img
            src={image}
            alt={`Imagen de ${product.title}`}
            loading="lazy"
            className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <ImageOff className="size-10" aria-hidden="true" />
          </div>
        )}

        {/* Type badge (top-left) */}
        <div className="absolute left-2 top-2">
          <Badge
            variant={typeBadge.variant}
            className="bg-background/85 backdrop-blur-sm"
            aria-hidden="true"
          >
            <typeBadge.icon className="size-3" aria-hidden="true" />
            {typeBadge.label}
          </Badge>
        </div>

 {/* — Recurrente badge (top-left, below the type badge).
            Renders ONLY when product.singlePurchaseRecurring is true. This
            is a UX affordance so buyers can identify single-purchase
            recurring products (suscripciones, membresías, consultoría)
            before clicking into the detail page. */}
        {product.singlePurchaseRecurring && (
          <Badge variant="secondary" className="absolute top-2 left-2 z-10">
            <Repeat className="size-3 mr-1" aria-hidden="true" />
            Recurrente
          </Badge>
        )}

        {/* Rating badge (top-right) — only when > 0 */}
        {hasRating && (
          <div className="absolute right-2 top-2">
            <Badge
              variant="secondary"
              className="bg-background/85 backdrop-blur-sm"
              aria-hidden="true"
            >
              <Star className="size-3 fill-yellow-400 text-yellow-400" aria-hidden="true" />
              {product.rating.toFixed(1)}
            </Badge>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 flex-col gap-2 p-3">
        {/* Category badge (UX affordance — full label in detail page) */}
        <Badge variant="outline" className="w-fit text-[10px] uppercase tracking-wide" aria-hidden="true">
          {categoryLabel}
        </Badge>

        {/* Title (2-line truncation) */}
        <h3
          className="line-clamp-2 text-sm font-semibold leading-snug text-foreground"
          title={product.title}
          aria-hidden="true"
        >
          {product.title}
        </h3>

        {/* Seller storeName (1-line truncation) */}
        <p
          className="truncate text-xs text-muted-foreground"
          title={product.seller.storeName}
          aria-hidden="true"
        >
          por {product.seller.storeName}
        </p>

 {/* U-3 ( Track A #7 — polish batch): Rating row near the
            title/price area. Shows "★ X.X" when the product has a rating > 0,
            or "Sin reseñas" in muted color when there are no reviews yet
            (Fase 3 returns `rating: 0` by default since reviews are Fase 6).
            This complements the compact rating badge on the image overlay
            (which only renders when rating > 0) by giving the buyer a
            consistent rating affordance in the body — including the
            "Sin reseñas" fallback so the absence of reviews is explicit
            (vs. simply missing UI). */}
        <div className="flex items-center gap-1 text-xs" aria-hidden="true">
          {hasRating ? (
            <>
              <Star className="size-3 fill-yellow-400 text-yellow-400" aria-hidden="true" />
              <span className="font-medium text-foreground">
                {product.rating.toFixed(1)}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">Sin reseñas</span>
          )}
        </div>

        {/* Price (in créditos) */}
        <div className="mt-auto pt-1 flex items-baseline gap-1">
          <span className="text-base font-bold text-foreground" aria-hidden="true">
            {product.priceCredits}
          </span>
          <span className="text-xs font-medium text-muted-foreground" aria-hidden="true">
            créditos
          </span>
        </div>
      </div>
    </Link>
  );
}
